import React, { useEffect, useRef, useState } from "react";
import { MapContainer, Marker, Popup, TileLayer, Tooltip, useMap, useMapEvents, LayersControl, Polygon, LayerGroup } from 'react-leaflet';
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import HomeAdd from '../../assets/icons/HomeAdd.svg';
import OfficeAdd from '../../assets/icons/OfficeAdd.svg';
import OtherAdd from '../../assets/icons/OtherAdd.svg';
import { useLocalization } from "../Context/MultiLanguageContext";
import { useLocation, useNavigate } from "react-router-dom";
import { getPamCodeSuggessionAsync } from "../../API/Address/GetPamCodeSuggessionAsync";
import OfficeIcon from '../../assets/icons/OfficeList.svg';
import OtherIcon from '../../assets/icons/OtherList.svg';
import HomeIcon from '../../assets/icons/HomeLIst.svg';
import ShareIcon from '../../assets/icons/share.svg';
import CopyIcon from '../../assets/icons/copy.svg';
import CheckIcon from '../../assets/icons/checked.svg';
import { EmailShareButton, WhatsappShareButton, FacebookShareButton, TwitterShareButton } from "react-share";
import { EmailIcon, WhatsappIcon, FacebookIcon, TwitterIcon } from "react-share";
import FavouriteTrue from '../../assets/icons/favouritetrue.svg';
import FavouriteFalse from '../../assets/icons/favouritefalse.svg';
import { AddtoFavouritesAsync } from "../../API/AddressFavourites/AddToFavouritesAsync";
import { RemoveFromFavouritesAsync } from "../../API/AddressFavourites/RemoveFromFavouritesAsync";
import { jwtDecode } from "jwt-decode";
import { toast } from "react-toastify";
import MarkerTooltip from "./MarkerTooltip";
import { Address as PamAddress } from '../../API/Address/GetPamCodeSuggessionAsync';
import ViewModal from './ViewModal';

interface BoundaryData {
  latitude: number;
  longitude: number;
}

interface TokenPayload {
  RoleId: number;
}

interface MapComponentProps {
  addresses?: PamAddress[];
  boundaryData?: { latitude: number; longitude: number }[];
  showLocateControl?: boolean;
  children?: React.ReactNode;
  onLocationChange?: (
    latitude: string,
    longitude: string,
    pinCode: string,
    street: string,
    city: string
  ) => void;
}

function getDistanceInMeters(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371e3; // Earth's radius in meters
  const φ1 = toRadians(lat1);
  const φ2 = toRadians(lat2);
  const Δφ = toRadians(lat2 - lat1);
  const Δλ = toRadians(lon2 - lon1);

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) *
    Math.sin(Δλ / 2) * Math.sin(Δλ / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  const distance = R * c;
  return distance; // in meters
}

function toRadians(degrees: number): number {
  return degrees * (Math.PI / 180);
}

const getCustomIcon = (addressType: string) => {
  let iconUrl;
  switch (addressType) {
    case "Home":
      iconUrl = HomeAdd;
      break;
    case "Office":
      iconUrl = OfficeAdd;
      break;
    default:
      iconUrl = OtherAdd;
  }
  return L.icon({
    iconUrl,
    iconSize: [40, 40],
    iconAnchor: [15, 30],
  });
};

const FitBoundsEffect: React.FC<{ bounds: L.LatLngBounds | null }> = ({ bounds }) => {
  const map = useMap();
  const hasFittedBounds = useRef(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (bounds && !hasFittedBounds.current) {
      timeoutRef.current = setTimeout(() => {
        if (map) {
          map.fitBounds(bounds, { padding: [20, 20] });
          hasFittedBounds.current = true;
        }
      }, 100);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [bounds, map]);

  return null;
};

const MapClickHandler: React.FC<{ onClick: (e: L.LeafletMouseEvent) => void }> = ({ onClick }) => {
  useMapEvents({
    click(e) {
      onClick(e);
    },
  });
  return null;
};

function getPolygonAreaInMeters(polygon: [number, number][]): number {
  if (polygon.length < 3) return 0;
  let area = 0;
  for (let i = 0; i < polygon.length; i++) {
    const [lat1, lon1] = polygon[i];
    const [lat2, lon2] = polygon[(i + 1) % polygon.length];

    const base = getDistanceInMeters(lat1, lon1, lat2, lon2);
    const avgLat = (lat1 + lat2) / 2;

    const width = getDistanceInMeters(avgLat, lon1, avgLat, lon2);
    area += (base * width) / 2;
  }
  return Math.abs(area);
}


function getPolygonLengthBreadth(polygon: [number, number][]) {
  if (polygon.length < 2) return { length: 0, breadth: 0 };
  let minLat = polygon[0][0], maxLat = polygon[0][0];
  let minLng = polygon[0][1], maxLng = polygon[0][1];
  polygon.forEach(([lat, lng]) => {
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
  });
  // Length: north-south (lat), Breadth: east-west (lng)
  const length = getDistanceInMeters(minLat, minLng, maxLat, minLng);
  const breadth = getDistanceInMeters(minLat, minLng, minLat, maxLng);
  return {
    length: parseFloat(length.toFixed(2)),
    breadth: parseFloat(breadth.toFixed(2))
  };

}

function getPolygonTopRight(polygon: [number, number][]) {
  if (polygon.length === 0) return [0, 0];
  let maxLat = polygon[0][0];
  let maxLng = polygon[0][1];
  polygon.forEach(([lat, lng]) => {
    if (lat > maxLat || (lat === maxLat && lng > maxLng)) {
      maxLat = lat;
      maxLng = lng;
    }
  });
  return [maxLat, maxLng];
}

const PolygonBlockMap: React.FC<MapComponentProps> = ({ addresses = [], boundaryData = [], onLocationChange, }) => {
  const [key, setKey] = useState(0);
  const { t } = useLocalization();
  const navigate = useNavigate();
  const location = useLocation();
  const [pincode, setPincode] = useState<string>('');
  const [street, setStreet] = useState<string>('');
  const [city, setCity] = useState<string>('');
  const [markerPosition, setMarkerPosition] = useState<[number, number] | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [addressResponse, setAddressResponse] = useState<PamAddress[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [copiedField, setCopiedField] = useState<{ type: "pamCode" | "digiPin"; value: string } | null>(null);
  const [addressResults, setAddressResults] = useState<PamAddress[]>([]);
  const [activeSharePamCode, setActiveSharePamCode] = useState<string | null>(null);
  const [localAddresses, setLocalAddresses] = useState<PamAddress[]>([]);
  const isFavouriteRoute = location.pathname.includes('/favourites');
  const [roleId, setRoleId] = useState<number | null>(null);


  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const decodedToken = jwtDecode<TokenPayload>(token);
        setRoleId(Number(decodedToken.RoleId));
      } catch (error) {
        // console.error('Error decoding token:', error);
      }
    }
  }, []);

  useEffect(() => {
  }, [roleId]);
  useEffect(() => {
    if (isFavouriteRoute) {
      setMarkerPosition([0, 0]);
    }
  }, [isFavouriteRoute]);

  useEffect(() => {
    setKey((prevKey) => prevKey + 1);
  }, [boundaryData, addresses]);



  const updateLocationDetails = (lat: number, lng: number) => {
    setMarkerPosition([lat, lng]);
    fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`)
      .then(response => response.json())
      .then(data => {
        const pinCodeValue = data.address?.postcode || 'N/A';
        const streetValue = data.address?.road || 'N/A';
        const cityValue = data.address?.city || data.address?.town || data.address?.village || 'N/A';

        setPincode(pinCodeValue);
        setStreet(streetValue);
        setCity(cityValue);

        if (onLocationChange) {
          onLocationChange(lat.toString(), lng.toString(), pinCodeValue, streetValue, cityValue);
        }
      })
      .catch((error) => console.error('Error fetching location details:', error));
  };

  const goToMapAddAddress = (latitude: number, longitude: number) => {
    if (roleId === 3) {
      toast.info("Please Log In to Add Address", { toastId: "guest-login" });
      return;
    }
    const params = new URLSearchParams({
      latitude: latitude.toString(),
      longitude: longitude.toString(),
      addressLine1: street || '',
      addressLine2: city || '',
      addressLine3: `${street}, ${city}` || '',
      pinCode: pincode || '',
    });
    navigate(`/add-new-address?${params.toString()}`);
  };


  const toggleFavourite = (id?: string) => {
    if (!id) return;
    setAddressResponse((prev) =>
      prev.map((address) =>
        address.id === id ? { ...address, isFavourite: !address.isFavourite } : address
      )
    );
  };

  const HeartClick = async (addressId: string, isFavourite: boolean) => {
    const newFavourite = !isFavourite;
    setAddressResponse((prevData) =>
      prevData.map((item) =>
        item.id === addressId ? { ...item, isFavourite: newFavourite } : item
      )
    );

    const token = localStorage.getItem('token');
    if (!token) {
      // console.error('Token not found. User might be logged out.');
      return;
    }

    try {
      const numericAddressId = Number(addressId);
      if (isNaN(numericAddressId)) {
        throw new Error("Invalid addressId: Cannot convert to number");
      }

      if (newFavourite) {
        await AddtoFavouritesAsync(numericAddressId, token);
      } else {
        await RemoveFromFavouritesAsync(numericAddressId, token);
      }
    } catch (error) {
      // console.error("An error occurred while updating favourite status:", error);
      revertFavouriteState(addressId, isFavourite);
    }
  };

  const revertFavouriteState = (addressId: string, originalState: boolean) => {
    setAddressResponse((prevData) =>
      prevData.map((item) =>
        item.id === addressId ? { ...item, isFavourite: originalState } : item
      )
    );
  };

  useEffect(() => {
    if (!isModalOpen) {
      setActiveSharePamCode(null);
    }
  }, [isModalOpen]);

  const defaultCenter: [number, number] =
    boundaryData.length > 0
      ? [boundaryData[0].latitude, boundaryData[0].longitude]
      : [9.9921, 76.3019];

  const polygonPositions: [number, number][] = boundaryData.map((point) => [point.latitude, point.longitude]);
  // Calculate area if polygon is valid
  const blockArea =
    polygonPositions.length >= 3 ? getPolygonAreaInMeters(polygonPositions) : 0;

  let bounds: L.LatLngBounds | null = null;
  const validAddresses = addresses.filter(
    (address) => address.addressLine1 && address.addressLine1.trim() !== ""
  );
  if (polygonPositions.length > 0) {
    const allPositions: L.LatLngExpression[] = [
      ...polygonPositions,
      ...validAddresses.map((address) => [Number(address.latitude), Number(address.longitude)] as [number, number])
    ];
    bounds = L.latLngBounds(allPositions);
  }

  const handleMapClick = (e: L.LeafletMouseEvent) => {
    const lat = e.latlng.lat;
    const lng = e.latlng.lng;
    updateLocationDetails(lat, lng);
  };


  const openGoogleMaps = (latitude: number, longitude: number) => {
    const defaultMap = localStorage.getItem("defaultMap") || "google";

    if (defaultMap === "waze") {
      const wazeMapsUrl = `https://waze.com/ul?ll=${latitude},${longitude}&navigate=yes`;
      window.open(wazeMapsUrl, '_blank');
    } else if (defaultMap === "mapindia") {
      const mapplsUrl = `https://www.mappls.com/@${latitude},${longitude}`;
      window.open(mapplsUrl, "_blank");
    } else if (defaultMap === "apple") {
      const appleMapsUrl = `https://maps.apple.com/?ll=${latitude},${longitude}&q=${latitude},${longitude}&z=18&t=m`;
      window.open(appleMapsUrl, "_blank");
    } else {
      const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
      window.open(googleMapsUrl, '_blank');
    }
  };


  const fetchAddresses = async () => {
    setIsSubmitting(true);
    const token = localStorage.getItem("token");
    if (!token) {
      // console.error("Token not found");
      setIsSubmitting(false);
      return;
    }
    try {
      const data = await getPamCodeSuggessionAsync(
        markerPosition ? markerPosition[0] : 0,
        markerPosition ? markerPosition[1] : 0,
        token,
        t
      );
      if (data === null) {
        setIsSubmitting(false);
        return;
      }
      // console.log("Fetched addresses:", data);
      if (Array.isArray(data) && data.length > 0) {
        // Data is an array of addresses — set it directly.
        setAddressResponse(data as PamAddress[]);
      } else if (data && !Array.isArray(data)) {
        // If API returns a single address object, normalize it into an array.
        setAddressResponse([data as PamAddress]);
      } else {
        console.warn("No data returned from PAM Code suggestions API or data has unexpected shape:", data);
        setAddressResponse([]);
      }

      setIsSubmitting(false);
      setIsModalOpen(true);
    } catch (error) {
      // console.error("Error fetching addresses:", error);
      setAddressResponse([]);
      setIsSubmitting(false);
    }
  };


  const handleCopy = async (value: string, type: "pamCode" | "digiPin") => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField({ type, value });
      setTimeout(() => setCopiedField(null), 2000);

    } catch (err) {
      // console.error('Failed to copy: ', err);

    }
  };


  const formatPamCode = (pamCode: string) => {
    if (!pamCode) return "N/A";
    const pincode = pamCode.slice(0, 6);
    const blockCode = pamCode.slice(6, 10);
    const addressCode = pamCode.slice(10);
    return `${pincode}-${blockCode}-${addressCode}`;
  };
  const shareMessage = (address: PamAddress) => {
    return `Check out this address:
    ${address.addressLine1}, ${address.addressLine2}, ${address.addressLine3}
    PAM Code: ${formatPamCode(address.pamCode || "")}
    DIGI PIN: ${address?.digiPin || "N/A"}

    
    More details: https://getmethere.in`;
  };

  function shareBlockCodeMessage(pinCode: string, blockCode: string, digiPin?: string): string {
    return `Block Code: ${pinCode}-${blockCode}
DIGI PIN: ${digiPin || "N/A"}

More details: https://getmethere.in`;
  }

  const showPolygon = polygonPositions.length >= 3;

  const handleMarkerClick = (lat: number, lng: number) => {
    const googleMapsUrl = `https://www.google.com/maps?q=${lat},${lng}`;
    window.open(googleMapsUrl, "_blank");
  };

  // ...inside your component, after polygonPositions...
  const { length, breadth } = getPolygonLengthBreadth(polygonPositions);
  const topRight = getPolygonTopRight(polygonPositions);

  return (
    <div className="h-full relative flex">
      <MapContainer key={key} center={defaultCenter} zoom={15} className="h-full w-full">
        <LayersControl position="topright">
          <LayersControl.BaseLayer name={t("defaultView")} checked>
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              maxNativeZoom={19}
              maxZoom={22}
              attribution='&copy; OpenStreetMap contributors'
            />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name={t("satelliteView")}>
            <LayerGroup>
              <TileLayer
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                maxNativeZoom={19}
                maxZoom={22}
                attribution="&copy; Esri"
              />
              <TileLayer
                url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
                attribution="&copy; Esri"
                opacity={0.8}
              />
            </LayerGroup>
          </LayersControl.BaseLayer>
        </LayersControl>

        {polygonPositions.length >= 3 && (
          <>
            <Polygon positions={polygonPositions} color="red" opacity={0.5} fillOpacity={0} />
            {/* Fixed block size box at the centroid of the polygon */}
            <PolygonBlockInfoDiv
              polygonPositions={polygonPositions}
              length={length}
              breadth={breadth}
            />
          </>
        )}

        {addresses.map((address) => {
          const lat = Number(address.latitude);
          const lng = Number(address.longitude);
          if (isNaN(lat) || isNaN(lng)) return null;
          return (
            <Marker
              key={address.id}
              position={[lat, lng]}
              icon={getCustomIcon(address.addressType)}
              eventHandlers={{
                click: () => openGoogleMaps(lat, lng),
              }}
            >
              <Popup>
                <div>
                  <strong>{t("address")}:</strong> {address.addressLine1}, {address.addressLine2}, {address.addressLine3}
                  <br />
                  <strong>{t("postalCode")}:</strong> {address.pinCode}
                </div>
              </Popup>
            </Marker>
          );
        })}

        {bounds && <FitBoundsEffect bounds={bounds} />}

        <MapClickHandler onClick={(e) => {
          const { lat, lng } = e.latlng;
          updateLocationDetails(lat, lng);
        }} />
        {markerPosition && (
          <Marker
            position={markerPosition}
            eventHandlers={{
              click: () => {
                // Show tooltip on click (if you want to control open/close state, add a state variable)
              },
            }}
          >
            <MarkerTooltip
              street={street}
              city={city}
              pincode={pincode}
              markerPosition={markerPosition}
              isSubmitting={isSubmitting}
              t={t}
              openGoogleMaps={openGoogleMaps}
              goToMapAddAddress={goToMapAddAddress}
              fetchAddresses={fetchAddresses}
              onClose={() => setMarkerPosition(null)}
            />
          </Marker>
        )}
      </MapContainer>

      <ViewModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        addressResponse={addressResponse}
        t={t}
        copiedField={copiedField}
        handleCopy={handleCopy}
        activeSharePamCode={activeSharePamCode}
        setActiveSharePamCode={setActiveSharePamCode}
        toggleFavourite={toggleFavourite}
        formatPamCode={formatPamCode}
        shareMessage={shareMessage}
        shareBlockCodeMessage={shareBlockCodeMessage}
      />
    </div >
  );
};

// Helper overlay component for block area info
const PolygonBlockInfoDiv: React.FC<{
  polygonPositions: [number, number][];
  length: number;
  breadth: number;
}> = ({ polygonPositions, length, breadth }) => {
  const map = useMap();
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const { t } = useLocalization();

  useEffect(() => {
    if (!map || polygonPositions.length < 1) return;
    // Find the topmost point (max latitude)
    let topPoint = polygonPositions[0];
    for (const pt of polygonPositions) {
      if (pt[0] > topPoint[0]) topPoint = pt;
    }
    const updatePos = () => {
      const containerPoint = map.latLngToContainerPoint(topPoint as [number, number]);
      setPos({ left: containerPoint.x, top: containerPoint.y });
    };
    updatePos();
    map.on('move zoom', updatePos);
    return () => {
      map.off('move zoom', updatePos);
    };
  }, [map, polygonPositions]);

  if (!pos) return null;

  return (
    <div
      style={{
        position: 'relative',
        left: pos.left,
        top: pos.top,
        background: "transparent",
        color: "grey",
        padding: "4px 10px",
        borderRadius: "6px",
        fontWeight: 600,
        fontSize: "12px",
        minWidth: 'unset',
        textAlign: 'center',
        lineHeight: 1.2,
        zIndex: 1000,
        pointerEvents: 'none',
        transform: 'translate(-50%, -100%)',
      }}
    >
      <span >{t("area")}</span>: {length} x {breadth} {t("m²")}
    </div>
  );
};

export default PolygonBlockMap;
